import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import {
  FRAMEWORKS,
  ASSESSMENT_QUESTIONS,
  type FrameworkId,
  type AssessmentQuestion,
  type AssessmentAnswer,
} from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';

export function AssessmentWizard() {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AssessmentAnswer>>({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const framework = frameworkId ? FRAMEWORKS[frameworkId as FrameworkId] : null;

  // Filter questions relevant to this framework
  const questions: AssessmentQuestion[] = (() => {
    if (!frameworkId) return ASSESSMENT_QUESTIONS;
    const relevant = ASSESSMENT_QUESTIONS.filter(
      (q) => frameworkId in q.frameworkTriggers
    );
    return relevant.length > 0 ? relevant : ASSESSMENT_QUESTIONS;
  })();

  // Load existing answers on mount
  useEffect(() => {
    if (!currentWorkspace || !frameworkId) return;
    loadAnswers();
  }, [currentWorkspace, frameworkId]);

  async function loadAnswers() {
    if (!currentWorkspace || !frameworkId) return;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<Record<string, AssessmentAnswer>>(
      `/api/tenants/${currentWorkspace.tenantId}/frameworks/${frameworkId}/assessment/answers`
    );

    if (apiError) {
      // If 404, that just means no answers yet
      if (apiError.code !== 'HTTP_404') {
        setError(apiError.message);
      }
    } else if (data) {
      setAnswers(data);
      // Find the first unanswered question to resume from
      const firstUnanswered = questions.findIndex((q) => !(q.id in data));
      if (firstUnanswered >= 0) {
        setCurrentIndex(firstUnanswered);
      }
    }
    setLoading(false);
  }

  const saveAnswer = useCallback(
    async (questionId: string, answer: AssessmentAnswer) => {
      if (!currentWorkspace || !frameworkId) return;
      setSaving(true);

      await apiClient.patch(
        `/api/tenants/${currentWorkspace.tenantId}/frameworks/${frameworkId}/assessment/answers`,
        { questionId, answer }
      );

      setSaving(false);
    },
    [currentWorkspace, frameworkId]
  );

  function setAnswer(questionId: string, answer: AssessmentAnswer) {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
    saveAnswer(questionId, answer);
  }

  function handleNext() {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
    }
  }

  function handleBack() {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  }

  async function handleSubmit() {
    if (!currentWorkspace || !frameworkId) return;
    setSubmitting(true);
    setError(null);

    const { error: apiError } = await apiClient.post(
      `/api/tenants/${currentWorkspace.tenantId}/frameworks/${frameworkId}/assessment/submit`,
      { answers }
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setCompleted(true);
    }
    setSubmitting(false);
  }

  if (!framework) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-center">
          <h1 className="text-2xl font-display font-bold text-white mb-2">Framework Not Found</h1>
          <p className="text-slate-400 mb-4">The framework "{frameworkId}" does not exist.</p>
          <Button variant="secondary" onClick={() => navigate('/assessments')}>
            Back to Assessments
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  // Completed state
  if (completed) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-growth/10 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-growth" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20,6 9,17 4,12" />
            </svg>
          </div>
          <h1 className="text-2xl font-display font-bold text-white mb-2">
            Assessment Complete
          </h1>
          <p className="text-slate-400 mb-6">
            You have completed the assessment for {framework.name}. Your deliverables are now being updated.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="secondary" onClick={() => navigate('/assessments')}>
              All Assessments
            </Button>
            <Button onClick={() => navigate(`/frameworks/${frameworkId}`)}>
              View Framework
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const answeredCount = questions.filter((q) => q.id in answers).length;
  const progressPct = Math.round((answeredCount / questions.length) * 100);
  const isLastQuestion = currentIndex === questions.length - 1;
  const allAnswered = answeredCount === questions.length;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Back link */}
      <Link to="/assessments" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15,18 9,12 15,6" />
        </svg>
        Back to Assessments
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: framework.color }}
          />
          <h1 className="text-xl font-display font-bold text-white">
            {framework.shortName} Assessment
          </h1>
        </div>
        <Badge variant="brand">
          {answeredCount}/{questions.length}
        </Badge>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-500">
            Question {currentIndex + 1} of {questions.length}
          </span>
          <span className="text-xs text-slate-400">{progressPct}%</span>
        </div>
        <div className="h-1.5 bg-surface-dark rounded-full overflow-hidden">
          <div
            className="h-full bg-brand rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Question card */}
      <Card padding="lg">
        <h2 className="text-lg font-display font-semibold text-white mb-6">
          {currentQuestion.question}
        </h2>

        <QuestionInput
          question={currentQuestion}
          value={answers[currentQuestion.id] ?? null}
          onChange={(value) => setAnswer(currentQuestion.id, value)}
        />

        {saving && (
          <p className="text-xs text-slate-500 mt-4">Saving...</p>
        )}
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          onClick={handleBack}
          disabled={currentIndex === 0}
        >
          Back
        </Button>

        <div className="flex gap-3">
          {isLastQuestion && allAnswered ? (
            <Button
              loading={submitting}
              onClick={handleSubmit}
            >
              Submit Assessment
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={isLastQuestion}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionInput({
  question,
  value,
  onChange,
}: {
  question: AssessmentQuestion;
  value: AssessmentAnswer;
  onChange: (value: AssessmentAnswer) => void;
}) {
  if (question.type === 'boolean') {
    const current = value as boolean | null;
    return (
      <div className="flex gap-3">
        <button
          onClick={() => onChange(true)}
          className={`flex-1 p-4 rounded-lg border text-sm font-medium transition-colors ${
            current === true
              ? 'border-brand bg-brand/5 text-white'
              : 'border-border bg-surface-dark text-slate-400 hover:border-slate-600'
          }`}
        >
          Yes
        </button>
        <button
          onClick={() => onChange(false)}
          className={`flex-1 p-4 rounded-lg border text-sm font-medium transition-colors ${
            current === false
              ? 'border-brand bg-brand/5 text-white'
              : 'border-border bg-surface-dark text-slate-400 hover:border-slate-600'
          }`}
        >
          No
        </button>
      </div>
    );
  }

  if (question.type === 'single' && question.options) {
    const current = value as string | null;
    return (
      <div className="space-y-2">
        {question.options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`w-full text-start p-3 rounded-lg border text-sm transition-colors ${
              current === opt.value
                ? 'border-brand bg-brand/5 text-white'
                : 'border-border bg-surface-dark text-slate-400 hover:border-slate-600'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  current === opt.value ? 'border-brand' : 'border-slate-500'
                }`}
              >
                {current === opt.value && (
                  <div className="w-2 h-2 rounded-full bg-brand" />
                )}
              </div>
              {opt.label}
            </div>
          </button>
        ))}
      </div>
    );
  }

  if (question.type === 'multi' && question.options) {
    const current = (value as string[] | null) || [];
    return (
      <div className="space-y-2">
        {question.options.map((opt) => {
          const isChecked = current.includes(opt.value);
          return (
            <button
              key={opt.value}
              onClick={() => {
                const next = isChecked
                  ? current.filter((v) => v !== opt.value)
                  : [...current, opt.value];
                onChange(next);
              }}
              className={`w-full text-start p-3 rounded-lg border text-sm transition-colors ${
                isChecked
                  ? 'border-brand bg-brand/5 text-white'
                  : 'border-border bg-surface-dark text-slate-400 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                    isChecked ? 'border-brand bg-brand' : 'border-slate-500'
                  }`}
                >
                  {isChecked && (
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20,6 9,17 4,12" />
                    </svg>
                  )}
                </div>
                {opt.label}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  return null;
}
